// O DISCADOR: as duas formas de discar uma fila e o que o gestor vê dela.
//
// Três coisas que o back separa em features distintas moram aqui porque a tela
// as usa juntas e elas falam do MESMO objeto — o lead na fila (campaign_lead):
//   - campanhas (/v1/campaigns): o console por campanha, em que o gestor monta
//     um mailing e o back serve o próximo elegível daquele mailing;
//   - relatório de fila (/v1/reports/queue): a ordem REAL em que a fila será
//     ligada, mais a gestão do gestor (remover, reativar, excluir um lead dela);
//   - discador automático (/v1/dialer): o power-dialer lead-centric, que serve a
//     fila da ORGANIZAÇÃO inteira, e a presença do vendedor nela.
// `QueueReportItem` é a prova de que é um domínio só: o relatório do gestor e o
// painel "próximos" do vendedor devolvem a MESMA linha.
//
// Fica de fora a REGRA da fila (peso, teto, cadência): é configuração global da
// organização e vive em ./queue-config.js. Os dois módulos não se importam.
import { api, toApiError } from "./client.js";

// Console de campanhas (rota /campaigns). Coexiste com o power-dialer de fila de
// leads em /dialer; aqui o back serve o próximo lead elegível de uma campanha,
// aplica ritmo/retry por categoria de tabulação e mantém a lista do-not-call.
export type CampaignStatus = "DRAFT" | "ACTIVE" | "PAUSED" | "DONE";
export type CampaignLeadState = "PENDING" | "IN_FLIGHT" | "DONE" | "STOPPED";

export interface CampaignFilter {
  leadStatus?: string[];
  source?: string[];
  assignedTo?: string[];
}

// Política de retry POR CAMPANHA (D2): só tempo/limites, sem peso (D4). Os PESOS
// por status seguem globais na org (QueueConfig). cadenceMinutes = espera por
// POSIÇÃO do retry (D8): [1ª, 2ª, 3ª+…]. Campos ausentes usam os defaults do back.
export interface RetryPolicy {
  maxAttemptsTotal?: number;
  maxAttemptsPerDay?: number;
  cadenceMinutes?: number[];
}

// A campanha é o mailing (nome + filtro + priority) + a política de retry (D2).
// Os PESOS por status seguem GLOBAIS na org (ver QueueConfig / /v1/queue-config).
export interface Campaign {
  id: string;
  organizationId: string;
  name: string;
  status: CampaignStatus;
  filter: CampaignFilter;
  priority: number;
  retryPolicy: RetryPolicy;
  createdAt: string;
  updatedAt: string;
}

export interface NextLead {
  campaignLeadId: string;
  lead: {
    id: string;
    name: string | null;
    // Telefone mascarado por papel: o vendedor não vê o número (phone null +
    // phoneHidden=true). Disca por campaignLeadId; `hasPhone` = discável.
    phone: string | null;
    phoneHidden: boolean;
    hasPhone: boolean;
    stage: string | null;
    status: string;
    attempts: number;
    lastDispositionCategory: string | null;
  };
}

export interface CampaignStats {
  byState: Record<CampaignLeadState, number>;
  byCategory: { category: string | null; count: number }[];
  totalTouched: number;
}

export async function getCampaigns(status?: CampaignStatus): Promise<Campaign[]> {
  const q = status ? `?status=${status}` : "";
  const { data, error } = await api<{ campaigns: Campaign[] }>(`/v1/campaigns${q}`);
  if (error) throw toApiError("GET /v1/campaigns", error);
  return data.campaigns;
}

export async function createCampaign(input: {
  name: string;
  filter: CampaignFilter;
  priority?: number;
  retryPolicy?: RetryPolicy;
}): Promise<Campaign> {
  const { data, error } = await api<Campaign>("/v1/campaigns", { method: "POST", body: input });
  if (error) throw toApiError("POST /v1/campaigns", error);
  return data;
}

export async function updateCampaign(
  id: string,
  patch: Partial<{
    name: string;
    status: CampaignStatus;
    filter: CampaignFilter;
    priority: number;
    retryPolicy: RetryPolicy;
  }>,
): Promise<Campaign> {
  const { data, error } = await api<Campaign>(`/v1/campaigns/${id}`, {
    method: "PATCH",
    body: patch,
  });
  if (error) throw toApiError("PATCH /v1/campaigns", error);
  return data;
}

export async function deleteCampaign(id: string): Promise<void> {
  const { error } = await api(`/v1/campaigns/${id}`, { method: "DELETE" });
  if (error) throw toApiError("DELETE /v1/campaigns", error);
}

// --- Relatório de Fila (discador) ---
// Espelha GET /v1/reports/queue. Só gestor. Read-only: a ordem real do discador.
export type QueueScope = "upcoming" | "done";

export interface QueueReportItem {
  leadId: string;
  name: string | null;
  phone: string | null;
  // Deep-link pronto para o registro do lead no HubSpot, montado no back a partir
  // do portal (env HUBSPOT_PORTAL_ID) + lead_external_id. Vem null quando não há
  // portal configurado ou o lead não tem external id — nesse caso o botão "Abrir
  // no HubSpot" não aparece. O front nunca conhece o formato da URL nem o portal.
  hubspotUrl: string | null;
  status: string;
  stage: string | null;
  attempts: number;
  lastDispositionCategory: string | null;
  // DESFECHO TÉCNICO da última tentativa (no_answer, busy, voicemail, carrier,
  // answered — e os terminais rejected/invalid_number). É o que a CADÊNCIA
  // consulta; lastDispositionCategory é a tabulação que o vendedor escolheu.
  // Null enquanto o lead não tem tentativa carimbada.
  lastAttemptOutcome: string | null;
  // Tentativas gastas NAQUELE desfecho — o teto é por desfecho, cada um com
  // contador próprio.
  outcomeAttempts: number;
  nextAttemptAt: string | null; // ISO
  assignedTo: string | null;
  agentName: string | null;
  // Campanha é só uma TAG do lead (associada no ingest) — pode não existir.
  campaignId: string | null;
  campaignName: string | null;
  // Estado de discagem do lead (campaign_lead), quando existe.
  campaignLeadId: string | null;
  // Ciclo de vida (visão do GESTOR): quando desativado/excluído e a hora de
  // reativação. Null quando ativo. Só o gestor vê/edita.
  reactivateAt: string | null; // ISO
  deactivatedAt: string | null; // ISO
  deactivatedByRule: string | null;
  excludedAt: string | null; // ISO
  // Quando o lead ENTROU NA ETAPA em que está — a coluna do quadro. Null quando
  // não há mudança de etapa registrada (lead antigo, ou etapa que veio no ingest
  // sem webhook): aí a tela mostra a entrada no sistema e diz que é outra coisa.
  stageEnteredAt: string | null; // ISO em UTC ("…Z")
  // Entrada do lead no sistema.
  createdAt: string; // ISO em UTC ("…Z")
  // A última coisa que aconteceu com o lead — tentativa OU mudança de etapa, o
  // que for mais novo. É daqui que sai o "parado há X" do card.
  lastMovementAt: string; // ISO em UTC ("…Z")
}

export interface QueueReport {
  items: QueueReportItem[];
  scope: QueueScope;
  limit: number;
}

// A fila pertence à organização. `includeInactive` = visão de GESTÃO (mostra
// desativados/excluídos com a hora de reativação).
export async function getQueueReport(params: {
  scope?: QueueScope;
  campaignId?: string;
  limit?: number;
  // `status` recorta UMA coluna da prévia kanban; `offset` pagina dentro dela.
  // A tabela do relatório não usa nenhum dos dois.
  status?: string;
  offset?: number;
  includeInactive?: boolean;
}): Promise<QueueReport> {
  const { data, error } = await api<QueueReport>("/v1/reports/queue", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/reports/queue", error);
  return data;
}

// Espelha GET /v1/reports/queue/by-status: o total REAL de cada coluna da prévia
// kanban. Vem separado da listagem porque a coluna pagina — o cabeçalho precisa
// do total da fila, não do tamanho da página carregada.
export interface QueueStatusTotals {
  totals: { status: string; total: number }[];
  scope: QueueScope;
}

export async function getQueueTotalsByStatus(
  params: { scope?: QueueScope; includeInactive?: boolean } = {},
): Promise<QueueStatusTotals> {
  const { data, error } = await api<QueueStatusTotals>("/v1/reports/queue/by-status", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/reports/queue/by-status", error);
  return data;
}

// --- Gestão da fila pelo GERENTE (por campaignLeadId; vendedor não acessa) ---
// Remove o lead da fila. Sem reactivateAt = remoção INDEFINIDA (só volta por
// reativação manual). Com reactivateAt (ISO) = volta sozinho na data.
export async function deactivateQueueLead(
  campaignLeadId: string,
  reactivateAt?: string,
  byRule?: string,
): Promise<void> {
  const { error } = await api("/v1/reports/queue/deactivate", {
    method: "POST",
    body: {
      campaignLeadId,
      ...(reactivateAt ? { reactivateAt } : {}),
      ...(byRule ? { byRule } : {}),
    },
  });
  if (error) throw toApiError("POST /v1/reports/queue/deactivate", error);
}

// Restaura o lead à fila (limpa desativação E exclusão).
export async function reactivateQueueLead(campaignLeadId: string): Promise<void> {
  const { error } = await api("/v1/reports/queue/reactivate", {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/reports/queue/reactivate", error);
}

// Soft delete indefinido.
export async function excludeQueueLead(campaignLeadId: string): Promise<void> {
  const { error } = await api("/v1/reports/queue/exclude", {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/reports/queue/exclude", error);
}

// --- Ações do vendedor DENTRO de uma campanha (rota /campaigns) ---
// Moravam sob o cabeçalho da regra de fila em api.ts, por acidente de ordem:
// são o console de campanha (servir, discar, pular, agendar, excluir,
// desativar, reativar, progresso), não configuração da organização.
export async function getNextLead(campaignId: string): Promise<NextLead | null> {
  const { data, error } = await api<{ next: NextLead | null }>(`/v1/campaigns/${campaignId}/next`);
  if (error) throw toApiError("GET /v1/campaigns/:id/next", error);
  return data.next;
}

export async function dialCampaignLead(
  campaignId: string,
  campaignLeadId: string,
): Promise<{ callId: string }> {
  const { data, error } = await api<{ callId: string }>(`/v1/campaigns/${campaignId}/dial`, {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/campaigns/:id/dial", error);
  return data;
}

export async function skipCampaignLead(campaignId: string, campaignLeadId: string): Promise<void> {
  const { error } = await api(`/v1/campaigns/${campaignId}/skip`, {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/campaigns/:id/skip", error);
}

// Retorno AGENDADO (D12): hora marcada (ISO). O back seta isScheduled + serve na
// hora (Tier B) e fura o teto diário. Dono do lead ou gestor+.
export async function scheduleCampaignLead(
  campaignId: string,
  campaignLeadId: string,
  scheduledAt: string,
): Promise<void> {
  const { error } = await api(`/v1/campaigns/${campaignId}/schedule`, {
    method: "POST",
    body: { campaignLeadId, scheduledAt },
  });
  if (error) throw toApiError("POST /v1/campaigns/:id/schedule", error);
}

// Exclusão (soft delete, GESTOR+, D17): tira o lead da fila desta campanha.
export async function excludeCampaignLead(
  campaignId: string,
  campaignLeadId: string,
): Promise<void> {
  const { error } = await api(`/v1/campaigns/${campaignId}/exclude`, {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/campaigns/:id/exclude", error);
}

// Desativação (pausa até reactivateAt ISO, GESTOR+, D16). Auto-reativa na data.
export async function deactivateCampaignLead(
  campaignId: string,
  campaignLeadId: string,
  reactivateAt: string,
  byRule?: string,
): Promise<void> {
  const { error } = await api(`/v1/campaigns/${campaignId}/deactivate`, {
    method: "POST",
    body: { campaignLeadId, reactivateAt, ...(byRule ? { byRule } : {}) },
  });
  if (error) throw toApiError("POST /v1/campaigns/:id/deactivate", error);
}

// Reativação manual (GESTOR+): volta o lead à fila já.
export async function reactivateCampaignLead(
  campaignId: string,
  campaignLeadId: string,
): Promise<void> {
  const { error } = await api(`/v1/campaigns/${campaignId}/reactivate`, {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/campaigns/:id/reactivate", error);
}

export async function getCampaignStats(campaignId: string): Promise<CampaignStats> {
  const { data, error } = await api<CampaignStats>(`/v1/campaigns/${campaignId}/stats`);
  if (error) throw toApiError("GET /v1/campaigns/:id/stats", error);
  return data;
}

// --- Discador automático (fila da organização, rota /dialer) ------------------
// Ciclo de vida servido a partir de TODOS os leads da organização (lead-centric,
// campanha-opcional) — o back serve o próximo lead elegível já ordenado
// (peso/devido/retry). Reusa NextLead/Disposition do discador por campanha; o
// vendedor disca por campaignLeadId (não vê o número). Ver back src/features/dialer.
export interface DialerClaimResult {
  next: NextLead | null;
  waitingForTurn: boolean;
}

export async function claimNextDialerLead(): Promise<DialerClaimResult> {
  const { data, error } = await api<DialerClaimResult>("/v1/dialer/claim", {
    method: "POST",
  });
  if (error) throw toApiError("POST /v1/dialer/claim", error);
  return data;
}

export type DialerAvailabilityStatus = "AVAILABLE" | "BUSY" | "UNAVAILABLE" | "OFFLINE";

export interface DialerAgentAvailability {
  userId: string;
  name: string;
  email: string;
  role: string;
  status: DialerAvailabilityStatus;
  canReceiveLead: boolean;
  inCall: boolean;
  lastSeenAt: string | null;
  statusChangedAt: string | null;
  availableSinceAt: string | null;
}

let fallbackDialerClientId: string | null = null;
const DIALER_CLIENT_ID_KEY = "lai-dialer-client-id";

// Identifica a ABA, não o usuário. Assim duas abas podem sinalizar presença sem
// que pausar/fechar uma delas derrube a outra.
export function getDialerClientId(): string {
  if (typeof window !== "undefined") {
    const existing = window.sessionStorage.getItem(DIALER_CLIENT_ID_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    window.sessionStorage.setItem(DIALER_CLIENT_ID_KEY, created);
    return created;
  }
  fallbackDialerClientId ??= crypto.randomUUID();
  return fallbackDialerClientId;
}

export async function getDialerAvailability(): Promise<DialerAgentAvailability> {
  const { data, error } = await api<{ availability: DialerAgentAvailability }>(
    "/v1/dialer/availability",
  );
  if (error) throw toApiError("GET /v1/dialer/availability", error);
  return data.availability;
}

export async function setDialerAvailability(
  available: boolean,
  clientId = getDialerClientId(),
): Promise<DialerAgentAvailability> {
  const { data, error } = await api<{ availability: DialerAgentAvailability }>(
    "/v1/dialer/availability",
    { method: "PUT", body: { clientId, available } },
  );
  if (error) throw toApiError("PUT /v1/dialer/availability", error);
  return data.availability;
}

export async function heartbeatDialerAvailability(
  clientId = getDialerClientId(),
): Promise<DialerAgentAvailability> {
  const { data, error } = await api<{ availability: DialerAgentAvailability }>(
    "/v1/dialer/availability/heartbeat",
    { method: "POST", body: { clientId } },
  );
  if (error) throw toApiError("POST /v1/dialer/availability/heartbeat", error);
  return data.availability;
}

// Linha do relatório de vendedores: o status AO VIVO (igual ao monitor da fila)
// somado ao tempo disponível ACUMULADO no período escolhido.
export interface SellerAvailabilityReport extends DialerAgentAvailability {
  availableSeconds: number;
  lastAvailableAt: string | null;
  lastCallAt: string | null;
  // Série por hora do período — só vem com `hourly`. Ausente é diferente de
  // vazia: ausente é "ninguém pediu", vazia é "não houve disponibilidade".
  //
  // `seconds` é o plantão da hora e `talkSeconds` o pedaço dele gasto FALANDO
  // (só ligação do discador interno). A espera é a diferença — o back não a
  // manda pronta porque as duas parcelas vêm de fontes diferentes.
  hours?: { at: string; seconds: number; talkSeconds: number }[];
}

// GET /v1/dialer/availability/report — gestor recebe a equipe inteira; vendedor,
// só a própria linha (escopo no back, igual ao resumo de chamadas).
export async function getSellerAvailabilityReport(
  params: { from?: string; to?: string; hourly?: boolean } = {},
): Promise<SellerAvailabilityReport[]> {
  const { data, error } = await api<{ sellers: SellerAvailabilityReport[] }>(
    "/v1/dialer/availability/report",
    { query: params },
  );
  if (error) throw toApiError("GET /v1/dialer/availability/report", error);
  return data.sellers;
}

export async function getDialerAgentAvailability(): Promise<DialerAgentAvailability[]> {
  const { data, error } = await api<{ agents: DialerAgentAvailability[] }>(
    "/v1/dialer/availability/agents",
  );
  if (error) throw toApiError("GET /v1/dialer/availability/agents", error);
  return data.agents;
}

// Quem está com cada lead EM VOO na organização agora.
export interface InFlightAssignment {
  campaignLeadId: string;
  leadName: string | null;
  userId: string;
  userName: string;
}

export interface DialerQueuePeek {
  items: QueueReportItem[];
  // Tamanho da fila INTEIRA (o `items` é só a página pedida em `limit`).
  // Contado no back com a mesma elegibilidade da listagem. Opcional porque back
  // anterior a esta rota não manda o campo.
  total?: number;
  inFlight: InFlightAssignment[];
  // O lead em voo DESTE vendedor (null = nenhum). É a resposta a "o lead do card
  // ainda é meu?" — o card é estado do navegador e não saberia sozinho.
  mine: string | null;
  // Quem está só esperando a cadência vencer e quando o primeiro volta. É o que
  // responde "dá pra ir no banheiro?" quando a fila esvazia. `proximoEm` é null
  // quando não há ninguém voltando — aí a fila acabou de verdade.
  aguardando: { total: number; proximoEm: string | null };
}

// Peek read-only dos próximos N leads da fila (para o painel "próximos"). Não
// trava. Reusa QueueReportItem. Vendedor recebe os números mascarados.
export async function getDialerQueue(limit = 20): Promise<DialerQueuePeek> {
  const { data, error } = await api<DialerQueuePeek>("/v1/dialer/queue", {
    query: { limit },
  });
  if (error) throw toApiError("GET /v1/dialer/queue", error);
  return data;
}

export async function dialDialerLead(campaignLeadId: string): Promise<{ callId: string }> {
  const { data, error } = await api<{ callId: string }>("/v1/dialer/dial", {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/dialer/dial", error);
  return data;
}

// POST /v1/dialer/dial-ai — a IA liga para o lead da fila (supervisionada), por
// campaignLeadId: registra a tentativa e linka a chamada ao lead, igual ao /dial
// humano. O vendedor ouve ao vivo e pode assumir (takeover). Ver back src/features/dialer.
export async function dialDialerLeadAi(campaignLeadId: string): Promise<{ callId: string }> {
  const { data, error } = await api<{ callId: string }>("/v1/dialer/dial-ai", {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/dialer/dial-ai", error);
  return data;
}

// A ligação deste lead já foi tabulada sozinha? O back tabula o que a telefonia
// entrega (não atendeu, recusou, número inválido) e o que a duração denuncia
// (caixa postal, ligação realizada) — ver features/call-finalize no back.
// `auto` = tabulada pelo sistema, não por uma pessoa.
export interface DialerLeadOutcome {
  tabulated: boolean;
  // A ligação já chegou a um estado final no back. finalized && !tabulated = o
  // sistema decidiu que quem tabula é o vendedor (houve conversa de verdade).
  finalized: boolean;
  label?: string;
  category?: string;
  auto?: boolean;
}

export async function getDialerLeadOutcome(campaignLeadId: string): Promise<DialerLeadOutcome> {
  const { data, error } = await api<DialerLeadOutcome>(
    `/v1/dialer/lead-outcome?campaignLeadId=${encodeURIComponent(campaignLeadId)}`,
  );
  if (error) throw toApiError("GET /v1/dialer/lead-outcome", error);
  return data ?? { tabulated: false, finalized: false };
}

export async function skipDialerLead(campaignLeadId: string): Promise<void> {
  const { error } = await api("/v1/dialer/skip", {
    method: "POST",
    body: { campaignLeadId },
  });
  if (error) throw toApiError("POST /v1/dialer/skip", error);
}
